import React, { useCallback, useEffect, useState } from "react";
import TableContainer from "Common/TableContainer";
import { Loader } from "../../Common/Loader";
import { Pencil, Trash2, Plus, Eye, EyeOff } from "lucide-react";
import DeleteModal from "Common/DeleteModal";
import { ToastContainer } from "react-toastify";
import { Common } from "helpers/common";
import moment from "moment";
import { AddUser } from "./AddUser";
import { getUserAdminList, deleteUserAdmin } from "helpers/backend_helper";

function UserList() {
  const [users, setUsers] = useState<any[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<any[]>([]);
  const [eventData, setEventData] = useState<any>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [searchText, setSearchText] = useState('');
  const [show, setShow] = useState<boolean>(false);
  const [userData, setUserData] = useState<any>({});
  const uiHelper = new Common();

  // Delete Modal
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const deleteToggle = () => setDeleteModal(!deleteModal);

  useEffect(() => {
    document.body.classList.remove('overflow-hidden');
    loadUsers(false);
  }, []);

  const roleOptions = [
    { value: 1, label: "Admin" },
    { value: 2, label: "Teacher" },
    { value: 3, label: "Parent" },
  ];
  
  async function loadUsers(reload: boolean) {
    try {
      const response = await getUserAdminList();
      console.log("Full user response:", response);
      setUsers(response.data);
      setFilteredUsers(response.data);
    } catch (error) {
      console.error("Error loading users:", error);
    } finally {
      setIsLoading(false);
    }
  }

  // Delete Data
  const onClickDelete = (cell: any) => {
    setDeleteModal(true);
    if (cell.userId) {
      setEventData(cell);
    }
  };

  const handleDelete = async () => {
    if (eventData && eventData.userId) {
      try {
        await deleteUserAdmin({ userId: eventData.userId });
        uiHelper.showSuccessMessage("User deleted successfully");
        loadUsers(true);
        setDeleteModal(false);
      } catch (error: any) {
        console.error("Error deleting user:", error);
        uiHelper.showErrorMessage("Error deleting user");
      }
    }
  };

  const handleEdit = (cell: any) => {
    setUserData(cell);
    setShow(true);
  };

  const handleAddUser = () => {
    setUserData({});
    setShow(true);
  };

  const filterSearchData = (e: any) => {
    const search = e.target.value.toLowerCase();
    setSearchText(search);
    
    if (search === '') {
      setFilteredUsers(users);
    } else {
      const filtered = users.filter((user: any) => 
        user.firstName?.toLowerCase().includes(search) ||
        user.lastName?.toLowerCase().includes(search) ||
        user.email?.toLowerCase().includes(search) ||
        user.loginId?.toLowerCase().includes(search) ||
        user.phone?.toLowerCase().includes(search)
      );
      setFilteredUsers(filtered);
    }
  };

  const onSave = (result: any) => {
    console.log("Save result:", result);
    loadUsers(true);
    toggle();
  };

  const toggle = useCallback(() => {
    setShow(!show);
    if (show) {
      setUserData({});
    }
  }, [show]);

  const columns = [
    {
      header: "First Name",
      accessorKey: "firstName",
      enableColumnFilter: false,
      enableSorting: false,
    },
    {
      header: "Last Name",
      accessorKey: "lastName",
      enableColumnFilter: false,
      enableSorting: false,
    },
    {
      header: "Login ID",
      accessorKey: "loginId",
      enableColumnFilter: false,
      enableSorting: false,
    },
    {
      header: "Email",
      accessorKey: "email",
      enableColumnFilter: false,
      enableSorting: false,
    },
    {
      header: "Phone",
      accessorKey: "phone",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }: any) => (
        <span>{row.original.phone || '-'}</span>
      ),
    },
    {
      header: "Role",
      accessorKey: "roleId",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }: any) => {
        const role = roleOptions.find(option => option.value === row.original.roleId);
        return (
          <span>{role ? role.label : `Role ${row.original.roleId}`}</span>
        );
      },
    },
    {
      header: "Status",
      accessorKey: "isActive",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }: any) => {
        const isActive = row.original.isActive;
        return (
          <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${
            isActive 
              ? "text-green-500 bg-green-100 dark:bg-green-500/20" 
              : "text-red-500 bg-red-100 dark:bg-red-500/20"
          }`}>
            {isActive ? <Eye className="size-3" /> : <EyeOff className="size-3" />}
            {isActive ? "Active" : "Inactive"}
          </span>
        );
      },
    },
    {
      header: "Created On",
      accessorKey: "createdOn",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }: any) => (
        <span>{row.original.createdOn ? moment(row.original.createdOn).format("MMM DD, YYYY") : '-'}</span>
      ),
    },
    {
      header: "Action",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }: any) => {
        return (
          <div className="flex gap-3">
            <button
              className="flex items-center justify-center size-8 transition-all duration-200 ease-linear rounded-md bg-slate-100 dark:bg-zink-600 dark:text-zink-200 text-slate-500 hover:text-custom-500 dark:hover:text-custom-500 hover:bg-custom-100 dark:hover:bg-custom-500/20"
              onClick={() => handleEdit(row.original)}
            >
              <Pencil className="size-4" />
            </button>
            <button
              className="flex items-center justify-center size-8 transition-all duration-200 ease-linear rounded-md bg-slate-100 dark:bg-zink-600 text-slate-500 dark:text-zink-200 hover:text-red-500 dark:hover:text-red-500 hover:bg-red-100 dark:hover:bg-red-500/20"
              onClick={() => onClickDelete(row.original)}
            >
              <Trash2 className="size-4" />
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <React.Fragment>
      <ToastContainer closeButton={false} limit={1} />
      <div className="grid grid-cols-1 gap-x-5 xl:grid-cols-12" style={{ marginTop: 15 }}>
        <div className="xl:col-span-12">
          <div className="card" id="userTable">

            <div className="flex items-center justify-between border-y border-dashed" style={{ padding: 10 }}>
              <div className="flex items-center gap-2 shrink-0">
                <h6 className="text-15">Filter </h6>
                <input
                  type="text"
                  className="w-full form-input  focus:outline-none focus:border-none focus:ring focus:ring-custom-100"
                  placeholder="User Name, Email, Login ID, or Phone"
                  value={searchText}
                  onChange={filterSearchData}
                />

                <div className="flex justify-end shrink-0">
                  <button
                    type="button"
                    className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
                    onClick={handleAddUser}
                    style={{ marginLeft: "auto" }}
                  >
                    <Plus className="inline-block size-4" />{" "}
                    <span className="align-middle">Add User</span>
                  </button>
                </div>
              </div>
            </div>


            {isLoading ? (
              <Loader />
            ) : (
              <TableContainer
                columns={columns}
                data={filteredUsers}
                isPagination={true}
                customPageSize={15}
                divclassName="overflow-x-auto"
                tableclassName="w-full whitespace-nowrap"
                theadclassName="ltr:text-left rtl:text-right bg-custom-200 dark:bg-zink-600"
                thclassName="px-2.5 py-1.5 font-semibold border-b border-slate-200 dark:border-zink-500"
                tdclassName="px-2.5 py-1.0 border-y border-slate-200 dark:border-zink-500"
                trclassName="even:bg-custom-50  dark:odd:bg-zink-700 dark:even:bg-zink-600"
                PaginationClassName="flex flex-col items-center gap-4 px-4 mt-3 md:flex-row"
              />
            )}

          </div>
        </div>
      </div>


      {userData ? (
        <AddUser 
          show={show} 
          onHide={toggle} 
          data={userData} 
          onSave={onSave}
        />) : (<></>)}

      {/* Delete Modal */}
      <DeleteModal
        show={deleteModal}
        onHide={deleteToggle}
        onDelete={handleDelete}
      />
    </React.Fragment>
  );
}

export default UserList;